import React, { useRef, useState } from "react";
import { Upload, FileText, ImageIcon, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

export default function FileUpload({
  label,
  description,
  value,            // { name, content_type, data, size }
  onChange,
  accept = "image/*,application/pdf",
  testId = "file-upload",
  icon: Icon = Upload,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error(`${file.name} is larger than 2 MB.`); return; }
    const ok = file.type.startsWith("image/") || file.type === "application/pdf";
    if (!ok) { toast.error("Use an image (PNG/JPG) or a PDF."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      // result is a data URL like "data:image/png;base64,XXXX" — strip the prefix.
      const result = reader.result || "";
      const base64 = typeof result === "string" ? result.split(",")[1] || "" : "";
      onChange({
        name: file.name,
        content_type: file.type,
        data: base64,
        size: file.size,
      });
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const preview = value?.data
    ? (value.content_type?.startsWith("image/")
      ? `data:${value.content_type};base64,${value.data}`
      : null)
    : null;

  return (
    <div data-testid={testId}>
      <div className="text-eyebrow text-[#4F4D45] mb-2">{label}</div>
      {value?.data ? (
        <div className="fv-card p-3 flex items-center gap-3" data-testid={`${testId}-preview`}>
          {preview ? (
            <img src={preview} alt={value.name} className="w-14 h-14 rounded-md object-cover border border-[#D2CDBE]" />
          ) : (
            <div className="w-14 h-14 rounded-md bg-[#F0ECE0] border border-[#D2CDBE] flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#1B3526]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{value.name}</div>
            <div className="text-xs text-[#4F4D45] flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-[#264C39]" />
              {(value.size / 1024).toFixed(0)} KB · ready
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={`${testId}-remove`}
            className="text-[#4F4D45] hover:text-[#992F2F] p-1.5 rounded-md hover:bg-[#F0ECE0]"
            aria-label="Remove file"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid={`${testId}-button`}
          className={`w-full text-left rounded-xl border-2 border-dashed p-5 flex items-center gap-4 transition-colors ${
            dragging ? "border-[#1B3526] bg-[#1B3526]/5" : "border-[#D2CDBE] bg-white hover:border-[#4F4D45]"
          }`}
        >
          <div className="w-11 h-11 rounded-lg bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center">
            <Icon className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-[#0A0A08]">Click or drop to upload</div>
            <div className="text-xs text-[#4F4D45] mt-0.5">{description || "PNG, JPG or PDF · up to 2 MB"}</div>
          </div>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
